import { useApolloClient } from "@apollo/client";
import { ModalOpenButton } from "@msys/ui";
import GroupRemoveIcon from "@mui/icons-material/GroupRemove";
import { Button } from "@mui/material";
import { Box, ButtonProps, Tooltip } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { useSnackbar } from "notistack";
import { ConfirmModal } from "../../../../commons/modals/ConfirmModal.js";
import {
  QuoteTemplateUnshareFromStoreButton_QuoteTemplateFragment,
  useUnshareQuoteTemplateFromStoreMutation,
} from "./QuoteTemplateUnshareFromStoreButton.generated.js";

interface Props {
  doc: QuoteTemplateUnshareFromStoreButton_QuoteTemplateFragment;
  buttonProps?: ButtonProps;
  refetchQueries?: string[];
}

export const QuoteTemplateUnshareFromStoreButton = ({
  doc,
  buttonProps,
  refetchQueries,
}: Props) => {
  const { t } = useTranslate(["Templates", "TemplatesSharing"]);
  const { enqueueSnackbar } = useSnackbar();

  const client = useApolloClient();
  const [unshare, { loading }] = useUnshareQuoteTemplateFromStoreMutation({
    client,
    refetchQueries,
    awaitRefetchQueries: true,
  });

  const tooltip = doc.hasSubscriptionToTemplate
    ? t("Template cannot be unpublished since it has subscribers", {
        ns: "TemplatesSharing",
      })
    : "";

  return (
    <Tooltip title={tooltip}>
      <Box display="flex" flexShrink={0} flexGrow={0}>
        <ModalOpenButton
          Modal={ConfirmModal}
          modalProps={{
            title: t(
              "Are you sure you want to unpublish this template from Meister Store?",
              {
                ns: "TemplatesSharing",
              }
            ),
            handleConfirm: async () => {
              await unshare({
                variables: { input: { quoteTemplateId: doc.id } },
              });
              enqueueSnackbar(
                t("Template unpublished from Store", {
                  ns: "TemplatesSharing",
                })
              );
            },
          }}
        >
          <Button
            loading={loading}
            startIcon={<GroupRemoveIcon />}
            variant={"contained"}
            color="warning"
            sx={{ whiteSpace: "nowrap" }}
            onClick={async e => {
              e.preventDefault();
              e.stopPropagation();
            }}
            {...buttonProps}
            disabled={doc.hasSubscriptionToTemplate || buttonProps?.disabled}
          >
            {t("Unpublish from Store", { ns: "TemplatesSharing" })}
          </Button>
        </ModalOpenButton>
      </Box>
    </Tooltip>
  );
};
